import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const SPECIALIZATIONS = ["iPhone", "iPad", "MacBook", "iMac"];

export default function FixerApplication() {
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    postal_code: "",
    specializations: [],
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [specError, setSpecError] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleSpec = (s) => {
    setForm((f) => ({
      ...f,
      specializations: f.specializations.includes(s)
        ? f.specializations.filter((x) => x !== s)
        : [...f.specializations, s],
    }));
    setSpecError(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.specializations.length === 0) {
      setSpecError(true);
      toast.error("Velg minst én enhetskategori");
      return;
    }
    setBusy(true);
    try {
      await api.post("/fixer-applications", form);
      setDone(true);
      toast.success("Søknad sendt — vi tar kontakt!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="tk-container py-24 max-w-xl text-center" data-testid="application-done">
        <div className="w-16 h-16 rounded-full bg-emerald-50 mx-auto mb-6 flex items-center justify-center">
          <CheckCircle2 className="text-emerald-600" size={32} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold mb-4">Takk for søknaden!</h1>
        <p className="text-slate-600 mb-8">
          Vi har sendt en bekreftelse til <strong>{form.contact_email}</strong>. Vi tar kontakt
          innen 2–3 virkedager og hjelper deg i gang som Tekkdeler repair point.
        </p>
        <Link to="/" className="tk-btn-secondary" data-testid="application-home-link">
          Tilbake til forsiden
        </Link>
      </div>
    );
  }

  return (
    <div className="tk-container py-16 max-w-2xl" data-testid="fixer-application-page">
      <p className="tk-overline mb-3">For verksteder</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-3">Bli et Tekkdeler repair point</h1>
      <p className="text-base text-slate-600 mb-10 leading-relaxed">
        Fyll inn skjemaet under, så tar vi kontakt. Som partner-verksted får du
        kvalitetsdeler levert til døren, betalingshåndtering, kundekommunikasjon og
        bestillinger fra hele Norge. Felt merket med <span className="text-red-500">*</span> er obligatoriske.
      </p>

      <form onSubmit={submit} className="space-y-4" data-testid="application-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700">
              Verkstedets navn <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              required
              className="tk-input mt-1"
              value={form.business_name}
              onChange={set("business_name")}
              placeholder="f.eks. iFix Oslo AS"
              data-testid="app-business-name"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">
              Kontaktperson <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              required
              className="tk-input mt-1"
              value={form.contact_name}
              onChange={set("contact_name")}
              placeholder="Ola Nordmann"
              data-testid="app-contact-name"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">
              E-post <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              required
              type="email"
              className="tk-input mt-1"
              value={form.contact_email}
              onChange={set("contact_email")}
              placeholder="post@verksted.no"
              data-testid="app-contact-email"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">
              Telefon <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              required
              className="tk-input mt-1"
              value={form.contact_phone}
              onChange={set("contact_phone")}
              placeholder="+47 …"
              data-testid="app-contact-phone"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-slate-700">
              Adresse <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              required
              className="tk-input mt-1"
              value={form.address}
              onChange={set("address")}
              placeholder="Storgata 12"
              data-testid="app-address"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">
              Postnummer <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              required
              className="tk-input mt-1"
              value={form.postal_code}
              onChange={set("postal_code")}
              placeholder="0184"
              data-testid="app-postal"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700">
            Enhetskategorier <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <p className="text-xs text-slate-500 mt-1">
            Velg minst én enhetskategori dere reparerer.
          </p>
          <div
            className={`flex flex-wrap gap-2 mt-2 p-2 rounded-md ${specError ? "ring-1 ring-red-400 bg-red-50/40" : ""}`}
            role="group"
            aria-required="true"
            aria-invalid={specError}
            data-testid="app-spec-group"
          >
            {SPECIALIZATIONS.map((s) => {
              const on = form.specializations.includes(s);
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleSpec(s)}
                  className={`px-3 py-1.5 rounded-md text-sm border ${on ? "bg-[#0B1C30] text-white border-[#0B1C30]" : "border-[#E2E8F0] bg-white text-slate-700"}`}
                  data-testid={`app-spec-${s}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
          {specError && (
            <p
              className="text-xs text-red-600 mt-2"
              role="alert"
              data-testid="app-spec-error"
            >
              Velg minst én enhetskategori
            </p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700">Om dere</label>
          <textarea
            className="tk-input mt-1 min-h-[100px]"
            value={form.notes}
            onChange={set("notes")}
            placeholder="Antall ansatte, åpningstider, sertifiseringer, etc."
            data-testid="app-notes"
          />
        </div>

        <button
          type="submit"
          disabled={busy}
          className="tk-btn-primary w-full justify-center"
          data-testid="app-submit-btn"
        >
          {busy ? "Sender …" : "Send søknad"}
        </button>
        <p className="text-xs text-slate-400 text-center">
          Vi behandler søknaden innen 2–3 virkedager og sender innloggingsdetaljer på e-post.
        </p>
      </form>
    </div>
  );
}
